import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CalendarPlus, Clock, Link2, MapPin, Printer, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Seo } from "@/components/Seo";
import { FadeUp } from "@/components/anim";
import { api } from "@/lib/api";
import { CONTACT, SITE_TAGLINE, STYLE_META, TERM, fmtTime } from "@/lib/site";
import { googleCalendarUrl, downloadIcs } from "@/lib/calendar";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const chipClass = (active) =>
  `px-4 h-11 inline-flex items-center text-xs font-bold uppercase tracking-[0.12em] border-[0.5px] transition-colors ${
    active
      ? "bg-[hsl(var(--kerrys-lime))] text-foreground border-foreground"
      : "bg-transparent text-muted-foreground border-foreground/40 hover:border-foreground hover:text-foreground"
  }`;

const AddToCalendar = ({ cls }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        className="rounded-none bg-transparent border-[0.5px] border-foreground/50 text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.1em] text-[10px] h-8 px-2.5 print:hidden"
        aria-label={`Add ${cls.name} to calendar`}
        data-testid="timetable-add-calendar-button"
      >
        <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />
        <span>Calendar</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="rounded-none border-[0.5px] border-foreground">
      <DropdownMenuItem
        onClick={() => window.open(googleCalendarUrl(cls), "_blank", "noopener")}
        className="text-xs font-bold uppercase tracking-[0.08em] cursor-pointer"
        data-testid="timetable-calendar-google"
      >
        Google Calendar
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => downloadIcs(cls)}
        className="text-xs font-bold uppercase tracking-[0.08em] cursor-pointer"
        data-testid="timetable-calendar-apple"
      >
        Apple / Outlook (.ics)
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export default function Timetable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [classes, setClasses] = useState(null);
  const [meta, setMeta] = useState({ styles: [], venues: [] });
  const [day, setDay] = useState(() => {
    const d = searchParams.get("day");
    if (!d) return "all";
    const match = DAY_ORDER.find((x) => x.toLowerCase() === d.toLowerCase());
    return match || "all";
  });
  const [venue, setVenue] = useState(() => searchParams.get("venue") || "all");
  const [style, setStyle] = useState(() => searchParams.get("style") || "all");

  // Keep filters in the URL so the view can be shared as a link
  useEffect(() => {
    const params = {};
    if (day !== "all") params.day = day;
    if (venue !== "all") params.venue = venue;
    if (style !== "all") params.style = style;
    setSearchParams(params, { replace: true });
  }, [day, venue, style, setSearchParams]);

  const copyShareLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere to share this view");
    } catch {
      toast.error("Couldn't copy — you can copy the address bar URL instead");
    }
  };

  useEffect(() => {
    api
      .get("/timetable")
      .then((res) => setClasses(res.data))
      .catch(() => setClasses([]));
    api
      .get("/timetable/meta")
      .then((res) => setMeta(res.data))
      .catch(() => {});
  }, []);

  const availableDays = useMemo(() => {
    const present = new Set((classes || []).map((c) => c.day));
    return DAY_ORDER.filter((d) => present.has(d));
  }, [classes]);

  const filtered = useMemo(() => {
    return (classes || []).filter(
      (c) => (day === "all" || c.day === day) && (venue === "all" || c.venue === venue) && (style === "all" || c.style === style)
    );
  }, [classes, day, venue, style]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((c) => {
      if (!map.has(c.day)) map.set(c.day, []);
      map.get(c.day).push(c);
    });
    return DAY_ORDER.filter((d) => map.has(d)).map((d) => [d, map.get(d)]);
  }, [filtered]);

  const hasFilters = day !== "all" || venue !== "all" || style !== "all";
  const reset = () => {
    setDay("all");
    setVenue("all");
    setStyle("all");
  };

  return (
    <>
      <Seo
        title="Class Timetable"
        description={"The weekly dance timetable at Kerry's, Whitstable — ISTD Ballet, Tap, Modern, Musical Theatre, Street Dance and Contemporary classes at The Umbrella Centre and the Whitstable Swimming Pool dance studio."}
        path="/timetable"
      />
      <section className="grain border-b-[0.5px] border-foreground/30 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24">
          <FadeUp className="max-w-3xl">
            <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground mb-6">
              Weekly &middot; term time &middot; Whitstable
            </p>
            <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
              The timetable
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Filter by day, venue or style &mdash; then grab a space online. It takes about two minutes. You can book
              any time; classes run term-time, starting with the new term on {TERM.start}.
            </p>
            <div className="mt-8 inline-flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun-pale))] px-5 py-3.5" data-testid="timetable-term-dates-banner">
              <p className="text-sm font-bold uppercase tracking-[0.12em]">{TERM.line}</p>
              <p className="text-sm text-foreground/70">{TERM.halfTermLine}</p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 print:py-0 print:px-0 print:max-w-none">
        {/* Print-only letterhead */}
        <div className="hidden print:block mb-8" data-testid="timetable-print-header">
          <div className="flex items-end justify-between border-b-2 border-black pb-4">
            <div>
              <p className="font-display uppercase text-5xl leading-none">
                Kerry&rsquo;s <span className="text-[hsl(var(--kerrys-coral))]">Timetable</span>
              </p>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mt-2">{SITE_TAGLINE} &middot; {TERM.shortLine}</p>
            </div>
            <div className="text-right text-xs leading-relaxed">
              <p className="font-bold">www.kerrys.dance</p>
              <p>{CONTACT.email} &middot; {CONTACT.phone}</p>
              <p className="text-[10px] mt-1">Printed {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} &mdash; always check online for the latest times</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="border-[0.5px] border-foreground bg-card p-4 sm:p-5 flex flex-col lg:flex-row gap-4 lg:items-center print:hidden" data-testid="timetable-filters">
          <div className="flex flex-wrap gap-2 flex-1" role="group" aria-label="Filter by day">
            <button onClick={() => setDay("all")} data-testid="timetable-filter-day-all" className={chipClass(day === "all")}>
              All days
            </button>
            {availableDays.map((d) => (
              <button key={d} onClick={() => setDay(d)} data-testid={`timetable-filter-day-${d.toLowerCase()}`} className={chipClass(day === d)}>
                {d}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={venue} onValueChange={setVenue}>
              <SelectTrigger className="w-full sm:w-[220px] rounded-none border-[0.5px] border-foreground/60 h-11 text-xs font-bold uppercase tracking-[0.1em]" data-testid="timetable-filter-venue-select">
                <SelectValue placeholder="Any venue" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">Any venue</SelectItem>
                {meta.venues.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger className="w-full sm:w-[190px] rounded-none border-[0.5px] border-foreground/60 h-11 text-xs font-bold uppercase tracking-[0.1em]" data-testid="timetable-filter-style-select">
                <SelectValue placeholder="Any style" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">Any style</SelectItem>
                {meta.styles.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" onClick={reset} className="rounded-none h-11 font-bold uppercase tracking-[0.1em] text-xs" data-testid="timetable-filters-reset-button">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground" data-testid="timetable-results-count">
            {classes === null ? "Loading classes…" : `Showing ${filtered.length} ${filtered.length === 1 ? "class" : "classes"}`}
          </p>
          {classes !== null && filtered.length > 0 && (
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={copyShareLink}
                className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-5"
                data-testid="timetable-share-button"
              >
                <Link2 className="h-4 w-4 mr-2" /> Copy link
              </Button>
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-5"
                data-testid="timetable-print-button"
              >
                <Printer className="h-4 w-4 mr-2" /> Print timetable
              </Button>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mt-8 space-y-16 print:mt-0 print:space-y-8" data-testid="timetable-results">
          {classes === null ? (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-16 rounded-none" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="border-[0.5px] border-dashed border-foreground/60 py-20 px-6 text-center" data-testid="timetable-empty-state">
              <p className="font-display uppercase text-4xl">Nothing matches those filters</p>
              <p className="text-muted-foreground mt-3">Try a different day or style &mdash; or clear the lot.</p>
              <Button
                onClick={reset}
                className="rounded-none mt-6 bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
                data-testid="timetable-empty-reset-button"
              >
                <RotateCcw className="h-4 w-4 mr-1.5" /> Clear filters
              </Button>
            </div>
          ) : (
            grouped.map(([d, list]) => (
              <div key={d}>
                <div className="flex items-end justify-between gap-4 border-b-[0.5px] border-foreground pb-3 mb-0">
                  <h2 className="font-display uppercase text-5xl sm:text-6xl leading-none">{d}</h2>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground pb-1.5">
                    {list.length} {list.length === 1 ? "class" : "classes"}
                  </p>
                </div>

                {/* Desktop table */}
                <div className="hidden md:block print:block border-[0.5px] border-t-0 border-foreground bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/60 hover:bg-muted/60">
                        <TableHead className="w-[140px] text-[11px] font-bold uppercase tracking-[0.15em]">Time</TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-[0.15em]">Class</TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-[0.15em]">Style</TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-[0.15em]">Ages</TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-[0.15em]">Level</TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-[0.15em]">Venue</TableHead>
                        <TableHead className="w-[110px] print:hidden"><span className="sr-only">Add to calendar</span></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {list.map((c) => {
                        const m = STYLE_META[c.style] || {};
                        return (
                          <TableRow key={c.id} data-testid="timetable-result-row" className="border-foreground/20">
                            <TableCell className="font-display text-xl tabular-nums whitespace-nowrap">
                              {fmtTime(c.start_time)}&ndash;{fmtTime(c.end_time)}
                            </TableCell>
                            <TableCell>
                              <p className="font-bold">{c.name}</p>
                              {c.notes && <p className="text-xs text-muted-foreground mt-0.5">{c.notes}</p>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`rounded-none border-[0.5px] font-bold text-xs ${m.badge || ""}`}>{c.style}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{c.age_group}</TableCell>
                            <TableCell className="text-muted-foreground">{c.level}</TableCell>
                            <TableCell className="text-muted-foreground">
                              <span className="flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-[hsl(var(--kerrys-sea))] shrink-0" />
                                {c.venue}
                              </span>
                            </TableCell>
                            <TableCell className="text-right print:hidden">
                              <AddToCalendar cls={c} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden print:hidden border-[0.5px] border-t-0 border-foreground divide-y-[0.5px] divide-foreground/30 bg-card">
                  {list.map((c) => {
                    const m = STYLE_META[c.style] || {};
                    return (
                      <div key={c.id} className="p-4" data-testid="timetable-result-row">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-bold">{c.name}</p>
                          <Badge variant="outline" className={`rounded-none border-[0.5px] shrink-0 font-bold text-xs ${m.badge || ""}`}>{c.style}</Badge>
                        </div>
                        <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1.5 font-display text-lg text-foreground">
                            <Clock className="h-3.5 w-3.5 text-[hsl(var(--kerrys-sea))]" />
                            {fmtTime(c.start_time)}&ndash;{fmtTime(c.end_time)}
                          </span>
                          <span>{c.age_group}</span>
                          {c.level && <span>{c.level}</span>}
                        </div>
                        <p className="mt-1.5 text-sm text-muted-foreground flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-[hsl(var(--kerrys-sea))] shrink-0" />
                          {c.venue}
                        </p>
                        <div className="mt-3">
                          <AddToCalendar cls={c} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <FadeUp className="mt-16">
          <div className="border-[0.5px] border-foreground bg-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid="timetable-singing-banner">
            <div>
              <p className="font-display uppercase text-3xl sm:text-4xl leading-none">Singing lessons</p>
              <p className="text-muted-foreground mt-2.5 text-sm sm:text-base max-w-xl">
                Wednesdays &middot; times TBC. Vocal coaching is joining the timetable &mdash; get in touch to register your interest and we&rsquo;ll save you a spot.
              </p>
            </div>
            <Button
              asChild
              variant="outline"
              className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6 shrink-0"
              data-testid="timetable-singing-contact-link"
            >
              <a href={CONTACT.emailHref}>Register interest</a>
            </Button>
          </div>
        </FadeUp>

        <FadeUp className="mt-10 print:hidden">
          <div className="border-[0.5px] border-foreground bg-secondary grain p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid="timetable-privates-banner">
            <div>
              <p className="font-display uppercase text-3xl sm:text-4xl leading-none">Private lessons</p>
              <p className="text-muted-foreground mt-2.5 text-sm sm:text-base max-w-xl">
                Wednesdays &amp; Fridays at Treetops Dance Studio &mdash; one-to-one, any age, any style. &pound;35 an hour with the venue included.
              </p>
            </div>
            <Button
              asChild
              variant="outline"
              className="rounded-none bg-card border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6 shrink-0"
              data-testid="timetable-privates-link"
            >
              <Link to="/private-lessons">
                How privates work <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </FadeUp>

        <FadeUp className="mt-10 print:hidden">
          <div className="bg-[hsl(var(--kerrys-coral))] border-[0.5px] border-foreground grain px-6 sm:px-12 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <div>
              <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92]">Spotted the one?</h2>
              <p className="text-foreground/80 mt-3 max-w-md">Spaces go quickly, especially the little ones&rsquo; classes. Book online and it&rsquo;s sorted.</p>
            </div>
            <Button
              asChild
              size="lg"
              className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-sm px-8 h-12 shrink-0"
              data-testid="timetable-book-cta-button"
            >
              <Link to="/book">
                Book now <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </FadeUp>
      </section>
    </>
  );
}
